/**
 * CreateBoardWizard — library-first parent onboarding (founder spec
 * 2026-05-24, Phase 4 rewrite).
 *
 * Linear, single-screen-at-a-time:
 *
 *   1. Build your child's library of words
 *      Parent pastes/types words their child already understands. Multi-
 *      line textarea + chip preview. Optional convenience layer.
 *
 *   2. Per-word loop (REQUIRED, one word at a time):
 *      For each word:
 *        - Add a picture (camera/upload/AI generate)
 *        - Record voice (mic, up to 5s)
 *        - Save and continue  → next word
 *      Both picture AND voice are REQUIRED — voice is what gets the
 *      parent's attention when the child presses the button.
 *
 *   3. Done building your library → AI suggestions
 *      AI proposes additional words from AAC core-vocab best practice.
 *      Parent accepts or skips each. Accepted words enter the SAME
 *      per-word loop (picture + voice).
 *
 *   4. Finish & create board (DISABLED until every word has picture+voice)
 *      Creates the profile + tiles in one shot, navigates to the new
 *      board. No "tile / version / board level" jargon shown to the
 *      parent during this flow.
 *
 * Persistence:
 *   - Word labels + metadata persisted to localStorage under a draft id
 *     so a closed tab returns to where the parent left off.
 *   - Photos + voice go to IndexedDB keyed `draft:${draftId}:${wordId}`.
 *     On finish, we re-key to `${profileId}:${tileId}` so they hydrate
 *     onto the new tiles automatically.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, ChevronRight, Image as ImageIcon,
  Loader2, Mic, Plus, Save, Sparkles, Trash2, X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { setSelectedBoardId } from "@/lib/selectedBoard";
import { useSEO } from "@/lib/seo";
import ImageUpload from "@/components/ImageUpload";
import AudioRecorder from "@/components/AudioRecorder";
import ParentalConsentModal from "@/components/ParentalConsentModal";
import {
  setLocalImage, setLocalAudio, getLocalMedia, deleteLocalMedia,
} from "@/lib/localMedia";

// ---------------------------------------------------------------------------
// Draft persistence
// ---------------------------------------------------------------------------
const DRAFT_LS_KEY = "sonspeak_library_draft_v1";

function newWordId() {
  return `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_LS_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.id || !Array.isArray(d.words)) return null;
    return d;
  } catch { return null; }
}
function saveDraft(d) {
  try { localStorage.setItem(DRAFT_LS_KEY, JSON.stringify(d)); } catch { /* noop */ }
}
function clearDraft() { try { localStorage.removeItem(DRAFT_LS_KEY); } catch { /* noop */ } }

function parseWordList(text) {
  return (text || "")
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s.length <= 60);
}

// ---------------------------------------------------------------------------
// Top-level container
// ---------------------------------------------------------------------------
export default function CreateBoardWizard() {
  useSEO({
    title: "Build your library — SonSpeak",
    description: "A simple, word-by-word flow that builds your child's first communication library.",
  });
  const nav = useNavigate();

  // Draft = { id, words:[{id,label,hasImage,hasAudio,fromAI,reason?}], step, focusIdx, boardLabel }
  const [draft, setDraft] = useState(() => loadDraft() || {
    id: `d_${Date.now().toString(36)}`,
    boardLabel: "",
    step: "list",                       // list | per-word | ai | finish
    focusIdx: 0,
    words: [],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Synchronous double-submit guard. Prevents a fast double-tap on
  // "Finish & create" from posting to /profiles twice and minting two
  // boards (Defect batch 3 #6, 2026-07-05).
  const submittingRef = useRef(false);
  // Phase 4 fix — COPPA consent gate. When POST /profiles returns
  // 423 with code=parental_consent_required, open the same modal
  // used on /profiles so the parent can complete verifiable consent
  // then we retry the create automatically.
  const [consentPrompt, setConsentPrompt] = useState(null);

  // Persist whenever draft changes.
  useEffect(() => { saveDraft(draft); }, [draft]);

  const allMediaReady = useMemo(
    () => draft.words.length > 0 && draft.words.every((w) => w.hasImage && w.hasAudio),
    [draft.words]
  );
  const missingMedia = useMemo(
    () => draft.words.filter((w) => !w.hasImage || !w.hasAudio).length,
    [draft.words]
  );

  // ----- Step transitions -------------------------------------------------
  const goList = () => setDraft((d) => ({ ...d, step: "list" }));
  const goPerWord = (idx = 0) => setDraft((d) => ({ ...d, step: "per-word", focusIdx: Math.max(0, Math.min(idx, d.words.length - 1)) }));
  const goAi = () => setDraft((d) => ({ ...d, step: "ai" }));
  const goFinish = () => setDraft((d) => ({ ...d, step: "finish" }));

  // ----- List edits -------------------------------------------------------
  const addWords = (text) => {
    const labels = parseWordList(text);
    if (labels.length === 0) return;
    setDraft((d) => {
      const seen = new Set(d.words.map((w) => w.label.toLowerCase()));
      const news = [];
      for (const label of labels) {
        const lower = label.toLowerCase();
        if (seen.has(lower)) continue;
        seen.add(lower);
        news.push({
          id: newWordId(), label, hasImage: false, hasAudio: false, fromAI: false,
        });
      }
      return { ...d, words: [...d.words, ...news] };
    });
  };
  const removeWord = async (wordId) => {
    await deleteLocalMedia(`draft-${draft.id}`, wordId);
    setDraft((d) => ({ ...d, words: d.words.filter((w) => w.id !== wordId) }));
  };

  // ----- Per-word media writes -------------------------------------------
  const setWordMedia = async (wordId, { image, audio }) => {
    const key = `draft-${draft.id}`;
    if (image !== undefined) await setLocalImage(key, wordId, image || null);
    if (audio !== undefined) await setLocalAudio(key, wordId, audio || null);
    setDraft((d) => ({
      ...d,
      words: d.words.map((w) => {
        if (w.id !== wordId) return w;
        return {
          ...w,
          ...(image !== undefined ? { hasImage: !!image } : {}),
          ...(audio !== undefined ? { hasAudio: !!audio } : {}),
        };
      }),
    }));
  };

  // ----- AI suggestions ---------------------------------------------------
  const [aiBusy, setAiBusy] = useState(false);
  const [aiList, setAiList] = useState([]); // [{label, reason, action: null|"accepted"|"skipped"}]
  const fetchAiSuggestions = async () => {
    setAiBusy(true);
    setError("");
    try {
      const { data } = await api.post("/coach/suggest-library-words", {
        known_words: draft.words.map((w) => w.label),
        n: 10,
      });
      const sugs = (data?.suggestions || []).map((s) => ({
        label: s.label, reason: s.reason || null, action: null,
      }));
      setAiList(sugs);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not fetch suggestions.");
    } finally {
      setAiBusy(false);
    }
  };
  const acceptSuggestion = (label) => {
    setAiList((arr) => arr.map((s) => s.label === label ? { ...s, action: "accepted" } : s));
    setDraft((d) => {
      const existing = new Set(d.words.map((w) => w.label.toLowerCase()));
      if (existing.has(label.toLowerCase())) return d;
      return {
        ...d,
        words: [...d.words, { id: newWordId(), label, hasImage: false, hasAudio: false, fromAI: true }],
      };
    });
  };
  const skipSuggestion = (label) =>
    setAiList((arr) => arr.map((s) => s.label === label ? { ...s, action: "skipped" } : s));

  // ----- Finish & create board -------------------------------------------
  const finishAndCreate = async () => {
    setError("");
    if (!allMediaReady) {
      setError(`Add a picture and a voice for every word first — ${missingMedia} still missing.`);
      return;
    }
    // Synchronous guard: React `busy` state can lag a fast second click.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      const labelInput = (draft.boardLabel || "").trim();
      const profileBody = {
        name: labelInput || "My board",
        avatar: "🌟",
        color: "#FFB627",
        kid_mode: true,
        sentence_builder_enabled: false,
      };
      const { data: profile } = await api.post("/profiles", profileBody);
      // The new profile already comes with a single "Core" category seeded.
      const cats = (await api.get(`/profiles/${profile.id}/categories`)).data || [];
      const coreCatId = cats[0]?.id;
      if (!coreCatId) throw new Error("Could not find the Core category — please try again.");

      // Create each word as a tile; move media from draft IndexedDB to
      // the real profile/tile keys so it hydrates next time the board
      // loads.
      const draftKey = `draft-${draft.id}`;
      for (let i = 0; i < draft.words.length; i++) {
        const w = draft.words[i];
        const media = await getLocalMedia(draftKey, w.id);
        const { data: tile } = await api.post(`/profiles/${profile.id}/tiles`, {
          label: w.label, emoji: "🔤", color: "#FFB627",
          category_id: coreCatId, is_favorite: false, is_gestalt: false,
          time_scope: "anytime", order: i,
        });
        if (media?.image_b64) await setLocalImage(profile.id, tile.id, media.image_b64);
        if (media?.audio_b64) await setLocalAudio(profile.id, tile.id, media.audio_b64);
        await deleteLocalMedia(draftKey, w.id);
      }

      setSelectedBoardId(profile.id);
      clearDraft();
      nav(`/board/${profile.id}`, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 423 && detail?.code === "parental_consent_required") {
        // Open the same modal /profiles uses. When consent is recorded,
        // we retry the finish flow automatically.
        setConsentPrompt({
          childFirstName: (draft.boardLabel || "your child").trim(),
          attestationPreview: detail.attestation_preview || "",
        });
        setBusy(false);
        submittingRef.current = false;
        return;
      }
      setError(formatApiErrorDetail(detail) || e.message);
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const onConsentRecorded = () => {
    setConsentPrompt(null);
    // Retry the same flow — /profiles POST will now succeed.
    finishAndCreate();
  };

  const startFresh = async () => {
    if (!window.confirm("Throw away this library and start over?")) return;
    const draftKey = `draft-${draft.id}`;
    for (const w of draft.words) await deleteLocalMedia(draftKey, w.id);
    clearDraft();
    setDraft({
      id: `d_${Date.now().toString(36)}`, boardLabel: "", step: "list", focusIdx: 0, words: [],
    });
    setAiList([]);
  };

  // ----- Render -----------------------------------------------------------
  return (
    <div className="page-wrap pt-6 pb-24" data-testid="library-wizard">
      <div className="max-w-2xl mx-auto px-4">
        <Link to="/pick-board" className="text-sm underline mb-3 inline-block">
          <ArrowLeft size={14} className="inline -mt-0.5" /> Back to boards
        </Link>

        <Stepper step={draft.step} />

        {error && (
          <div className="brutal-card bg-red-50 p-3 text-sm text-red-700 mb-3" data-testid="library-wizard-error">
            {error}
          </div>
        )}

        {draft.step === "list" && (
          <ListStep
            draft={draft}
            setBoardLabel={(s) => setDraft((d) => ({ ...d, boardLabel: s }))}
            addWords={addWords}
            removeWord={removeWord}
            onContinue={() => {
              if (draft.words.length === 0) {
                setError("Add at least one word your child already knows.");
                return;
              }
              setError("");
              goPerWord(draft.words.findIndex((w) => !w.hasImage || !w.hasAudio) >= 0
                ? draft.words.findIndex((w) => !w.hasImage || !w.hasAudio)
                : 0);
            }}
            onDoneLibrary={() => {
              setError("");
              goAi();
              if (aiList.length === 0) fetchAiSuggestions();
            }}
            startFresh={startFresh}
            missingMedia={missingMedia}
            allMediaReady={allMediaReady}
          />
        )}

        {draft.step === "per-word" && draft.words[draft.focusIdx] && (
          <PerWordStep
            word={draft.words[draft.focusIdx]}
            indexInfo={{ current: draft.focusIdx + 1, total: draft.words.length }}
            draftKey={`draft-${draft.id}`}
            onMediaChange={(patch) => setWordMedia(draft.words[draft.focusIdx].id, patch)}
            onPrev={() => goPerWord(draft.focusIdx - 1)}
            onNext={() => {
              const nextMissing = draft.words.findIndex((w, i) => i > draft.focusIdx && (!w.hasImage || !w.hasAudio));
              if (nextMissing >= 0) {
                goPerWord(nextMissing);
              } else {
                // back to list to show overall state
                goList();
              }
            }}
            onBackToList={goList}
          />
        )}

        {draft.step === "ai" && (
          <AiStep
            aiBusy={aiBusy}
            suggestions={aiList}
            onAccept={acceptSuggestion}
            onSkip={skipSuggestion}
            onRefresh={fetchAiSuggestions}
            onBackToList={goList}
            onContinue={() => {
              setError("");
              if (!allMediaReady) {
                // Send parent to fill missing media first.
                const idx = draft.words.findIndex((w) => !w.hasImage || !w.hasAudio);
                goPerWord(Math.max(0, idx));
              } else {
                goFinish();
              }
            }}
          />
        )}

        {draft.step === "finish" && (
          <FinishStep
            words={draft.words}
            boardLabel={draft.boardLabel}
            setBoardLabel={(s) => setDraft((d) => ({ ...d, boardLabel: s }))}
            allMediaReady={allMediaReady}
            missingMedia={missingMedia}
            busy={busy}
            onBack={goAi}
            onFinish={finishAndCreate}
            onFillMissing={() => {
              const idx = draft.words.findIndex((w) => !w.hasImage || !w.hasAudio);
              goPerWord(Math.max(0, idx));
            }}
          />
        )}
      </div>
      {consentPrompt && (
        <ParentalConsentModal
          childFirstName={consentPrompt.childFirstName}
          attestationPreview={consentPrompt.attestationPreview}
          returnTo="/create-board"
          onClose={() => setConsentPrompt(null)}
          onConsentRecorded={onConsentRecorded}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function Stepper({ step }) {
  const STEPS = [
    { id: "list", label: "Your library" },
    { id: "per-word", label: "Picture + voice" },
    { id: "ai", label: "Suggestions" },
    { id: "finish", label: "Create board" },
  ];
  const order = ["list", "per-word", "ai", "finish"];
  const idx = order.indexOf(step);
  return (
    <ol className="flex items-center gap-1 mb-5 flex-wrap" data-testid="library-wizard-stepper">
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <li
            key={s.id}
            className={`text-xs font-extrabold px-2 py-1 rounded-md ${
              active ? "bg-[color:var(--gold)]" : done ? "bg-[color:var(--cream)]" : "bg-[color:var(--paper)] text-[color:var(--ink)]/60"
            }`}
            aria-current={active ? "step" : undefined}
            data-testid={`library-step-${s.id}`}
          >
            {done ? <Check size={12} className="inline -mt-0.5" /> : null} {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function ListStep({ draft, setBoardLabel, addWords, removeWord, onContinue, onDoneLibrary, startFresh, missingMedia, allMediaReady }) {
  const [text, setText] = useState("");
  const taRef = useRef(null);
  const onAdd = () => {
    if (!text.trim()) return;
    addWords(text);
    setText("");
    taRef.current?.focus();
  };
  return (
    <div className="brutal-card bg-white p-5" data-testid="library-step-list-pane">
      <h1 className="display text-2xl sm:text-3xl font-extrabold mb-1">
        Build your child's library of words
      </h1>
      <p className="text-sm text-[color:var(--ink-soft)] mb-4">
        Type the words and phrases your child already understands. One per
        line, or separated by commas. You can paste a list.
      </p>

      <label className="block mb-2">
        <span className="font-semibold text-sm mb-1 block">Board label (just for you)</span>
        <input
          className="brutal-input w-full max-w-sm"
          value={draft.boardLabel}
          onChange={(e) => setBoardLabel(e.target.value)}
          placeholder="e.g. Buddy, Student A, Home board"
          maxLength={40}
          data-testid="library-board-label"
        />
        <span className="text-[11px] text-[color:var(--ink)]/60">
          Use a nickname, not your child's real name. The real name (if you
          want to enter it) stays encrypted on your device in Parental
          Settings.
        </span>
      </label>

      <label className="block mb-2">
        <span className="font-semibold text-sm mb-1 block">Add words</span>
        <textarea
          ref={taRef}
          className="brutal-input w-full"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"want\nmore\nstop\ngo\nhelp"}
          data-testid="library-word-input"
        />
      </label>
      <div className="flex justify-end mb-4">
        <button type="button" onClick={onAdd} className="pill-btn primary" data-testid="library-word-add">
          <Plus size={14} /> Add to library
        </button>
      </div>

      {draft.words.length === 0 ? (
        <p className="text-sm text-[color:var(--ink)]/50 italic mb-4">
          No words yet. Add the words your child already knows above.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2 mb-4" data-testid="library-word-list">
          {draft.words.map((w) => (
            <li
              key={w.id}
              className={`pill-btn ${w.hasImage && w.hasAudio ? "bg-[color:var(--cream)]" : "bg-white"} flex items-center gap-1 text-sm`}
              data-testid={`library-word-chip-${w.id}`}
            >
              <span>{w.label}</span>
              {w.fromAI && <span className="text-[10px] uppercase font-extrabold text-[color:var(--coral)]">AI</span>}
              <span className="text-[10px] flex items-center gap-0.5 text-[color:var(--ink)]/60" title="Picture / Voice status">
                <ImageIcon size={10} className={w.hasImage ? "text-green-600" : "text-red-500"} />
                <Mic size={10} className={w.hasAudio ? "text-green-600" : "text-red-500"} />
              </span>
              <button
                type="button"
                onClick={() => removeWord(w.id)}
                className="text-[color:var(--ink)]/40 hover:text-red-600"
                aria-label={`Remove ${w.label}`}
                data-testid={`library-word-remove-${w.id}`}
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {draft.words.length > 0 && (
        <div className="brutal-card bg-[color:var(--paper-muted)] p-3 text-sm mb-4">
          <strong>{draft.words.length}</strong> word{draft.words.length === 1 ? "" : "s"} in your library.
          {!allMediaReady && (
            <> {missingMedia} still need a picture or voice.</>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <button type="button" onClick={startFresh} className="pill-btn text-red-700" data-testid="library-start-over">
          <Trash2 size={14} /> Start over
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onContinue}
            disabled={draft.words.length === 0}
            className="pill-btn primary"
            data-testid="library-continue-per-word"
          >
            {missingMedia > 0 ? "Add pictures & voice" : "Review media"} <ArrowRight size={14} />
          </button>
          <button
            type="button"
            onClick={onDoneLibrary}
            disabled={draft.words.length === 0}
            className="pill-btn"
            data-testid="library-done"
          >
            Done — see AI suggestions <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function PerWordStep({ word, indexInfo, draftKey, onMediaChange, onPrev, onNext, onBackToList }) {
  const [image, setImage] = useState(null);
  const [audio, setAudio] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from IndexedDB when entering the screen.
  useEffect(() => {
    let cancelled = false;
    setHydrated(false);
    (async () => {
      const local = await getLocalMedia(draftKey, word.id);
      if (cancelled) return;
      setImage(local?.image_b64 || null);
      setAudio(local?.audio_b64 || null);
      setHydrated(true);
    })();
    return () => { cancelled = true; };
  }, [draftKey, word.id]);

  const ready = !!image && !!audio;

  const setImg = async (b64) => {
    setImage(b64 || null);
    await onMediaChange({ image: b64 || null });
  };
  const setAud = async (b64) => {
    setAudio(b64 || null);
    await onMediaChange({ audio: b64 || null });
  };

  return (
    <div className="brutal-card bg-white p-5" data-testid="library-step-per-word-pane">
      <div className="flex items-center justify-between mb-2">
        <h1 className="display text-2xl sm:text-3xl font-extrabold">
          {word.label}
        </h1>
        <span className="text-xs text-[color:var(--ink-soft)] font-semibold" data-testid="library-per-word-counter">
          {indexInfo.current} of {indexInfo.total}
        </span>
      </div>
      <p className="text-sm text-[color:var(--ink-soft)] mb-4">
        Add a picture and record your voice for <strong>{word.label}</strong>.
        Your voice is what tells you the child needs something — that's why
        we need both before we make the board.
      </p>

      <div className="brutal-card bg-[color:var(--cream)] p-3 mb-3">
        <div className="font-extrabold mb-2 flex items-center gap-2">
          <ImageIcon size={16} /> Picture
          {image && <span className="text-xs text-green-700 font-bold">✓ added</span>}
        </div>
        {hydrated && (
          <ImageUpload
            value={image}
            onChange={setImg}
            aiLabel={word.label}
            profileId={null}
          />
        )}
        <p className="text-[11px] text-[color:var(--ink)]/60 mt-2">
          Saved only on this device. Never sent to the cloud.
        </p>
      </div>

      <div className="brutal-card bg-[color:var(--paper-muted)] p-3 mb-4">
        <div className="font-extrabold mb-2 flex items-center gap-2">
          <Mic size={16} /> Voice
          {audio && <span className="text-xs text-green-700 font-bold">✓ recorded</span>}
        </div>
        {hydrated && (
          <AudioRecorder value={audio} onChange={setAud} profileId={null} />
        )}
        <p className="text-[11px] text-[color:var(--ink)]/60 mt-2">
          Saved only on this device. The child hears YOUR voice say
          "{word.label}".
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={indexInfo.current === 1}
          className="pill-btn"
          data-testid="library-per-word-prev"
        >
          <ArrowLeft size={14} /> Previous
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onBackToList}
            className="pill-btn"
            data-testid="library-per-word-back"
          >
            Back to list
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!ready}
            className="pill-btn primary"
            data-testid="library-per-word-save-continue"
            title={ready ? undefined : "Add a picture AND record your voice to continue"}
          >
            Save & continue <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function AiStep({ aiBusy, suggestions, onAccept, onSkip, onRefresh, onBackToList, onContinue }) {
  return (
    <div className="brutal-card bg-white p-5" data-testid="library-step-ai-pane">
      <h1 className="display text-2xl sm:text-3xl font-extrabold mb-1 flex items-center gap-2">
        <Sparkles size={22} aria-hidden /> AI suggestions
      </h1>
      <p className="text-sm text-[color:var(--ink-soft)] mb-4">
        Based on what your child already knows, here are useful words from
        AAC core-vocabulary best practice. Accept any that fit — accepted
        words will go through the same picture and voice step.
      </p>

      {aiBusy ? (
        <div className="text-sm text-[color:var(--ink)]/60 flex items-center gap-2 my-6" data-testid="library-ai-busy">
          <Loader2 size={16} className="animate-spin" /> Thinking…
        </div>
      ) : suggestions.length === 0 ? (
        <p className="text-sm text-[color:var(--ink)]/60 mb-4">
          No suggestions yet.{" "}
          <button type="button" className="underline" onClick={onRefresh}>
            Ask the coach
          </button>
        </p>
      ) : (
        <ul className="space-y-2 mb-4" data-testid="library-ai-list">
          {suggestions.map((s) => (
            <li
              key={s.label}
              className={`brutal-card p-3 ${s.action === "accepted" ? "bg-[color:var(--cream)]" : s.action === "skipped" ? "opacity-50" : "bg-white"} flex items-start gap-3`}
              data-testid={`library-ai-suggestion-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="flex-1">
                <div className="font-extrabold">{s.label}</div>
                {s.reason && (
                  <div className="text-xs text-[color:var(--ink-soft)] mt-1">{s.reason}</div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {s.action === "accepted" ? (
                  <span className="pill-btn primary" aria-disabled>
                    <Check size={12} /> Added
                  </span>
                ) : s.action === "skipped" ? (
                  <span className="pill-btn" aria-disabled>Skipped</span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => onAccept(s.label)}
                      className="pill-btn primary"
                      data-testid={`library-ai-accept-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <Check size={12} /> Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => onSkip(s.label)}
                      className="pill-btn"
                      data-testid={`library-ai-skip-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      Skip
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap justify-between items-center gap-2">
        <button type="button" onClick={onBackToList} className="pill-btn" data-testid="library-ai-back">
          <ArrowLeft size={14} /> Back to library
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onRefresh} disabled={aiBusy} className="pill-btn" data-testid="library-ai-refresh">
            <Sparkles size={14} /> {suggestions.length ? "More" : "Suggest words"}
          </button>
          <button type="button" onClick={onContinue} className="pill-btn primary" data-testid="library-ai-continue">
            Continue <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function FinishStep({ words, boardLabel, setBoardLabel, allMediaReady, missingMedia, busy, onBack, onFinish, onFillMissing }) {
  return (
    <div className="brutal-card bg-white p-5" data-testid="library-step-finish-pane">
      <h1 className="display text-2xl sm:text-3xl font-extrabold mb-1">
        Ready to create your child's board?
      </h1>
      <p className="text-sm text-[color:var(--ink-soft)] mb-4">
        Your library has {words.length} word{words.length === 1 ? "" : "s"}.{" "}
        {allMediaReady ? "Every word has a picture and your voice." : `${missingMedia} still need a picture or voice.`}
      </p>

      <label className="block mb-4">
        <span className="font-semibold text-sm mb-1 block">Board label</span>
        <input
          className="brutal-input w-full max-w-sm"
          value={boardLabel}
          onChange={(e) => setBoardLabel(e.target.value)}
          placeholder="e.g. My board"
          maxLength={40}
          data-testid="library-finish-board-label"
        />
      </label>

      <ul className="flex flex-wrap gap-2 mb-4" data-testid="library-finish-summary">
        {words.map((w) => (
          <li
            key={w.id}
            className={`pill-btn ${w.hasImage && w.hasAudio ? "bg-[color:var(--cream)]" : "bg-amber-50"}`}
          >
            {w.label}{" "}
            <ImageIcon size={10} className={w.hasImage ? "text-green-600" : "text-amber-700"} />
            <Mic size={10} className={w.hasAudio ? "text-green-600" : "text-amber-700"} />
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={onBack} className="pill-btn" disabled={busy} data-testid="library-finish-back">
          <ArrowLeft size={14} /> Back
        </button>
        {allMediaReady ? (
          <button
            type="button"
            onClick={onFinish}
            disabled={busy}
            className="pill-btn primary"
            data-testid="library-finish-create"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><Save size={14} /> Finish & create board</>}
          </button>
        ) : (
          <button
            type="button"
            onClick={onFillMissing}
            className="pill-btn primary"
            data-testid="library-finish-fill"
          >
            Add missing pictures & voices <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
